import { useContext, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button, InlineAlert } from '../components/BaseComponents'
import routes from '../components/Router/routes'
import { TEXT_LINK_FOCUS_RING } from '../lib/a11yFocus'
import UniversalPageLayout from '../components/BaseComponents/UniversalPageLayout/UniversalPageLayout'
import { getApiErrorMessage, getCSRFToken } from '../lib/utils'
import useApi from '../hooks/useApi'
import { userContext } from '../context/UserContext'
import OrganisationUnlockRequestModal from '../components/OrganisationUnlockRequestModal/OrganisationUnlockRequestModal'
import { dashedBorderBackground } from '../lib/dashedBorder'

export type MembershipSummary = {
	membershipId: string
	companyId: string
	companyName: string
	roleName: string
	companyLocked?: boolean
	fictitiousKind: 'training' | 'demo' | 'sample' | null
}

type SelectOrganisationProps = {
	fullName?: string
	memberships: MembershipSummary[]
	// Global preference (see CompanySettings); UI-only. With no real organisation, hiding fictitious
	// ones leaves nothing to pick, so a holding page is shown instead of the list.
	showFictitiousOrganisations?: boolean
}

const ROLE_LABELS: Record<string, string> = {
	company: 'Company user',
	analyst: 'Analyst',
	auditor: 'Auditor',
	admin: 'Administrator',
}

const FICTITIOUS_KIND_LABELS: Record<string, string> = {
	training: 'Training organisation',
	demo: 'Demo organisation',
	sample: 'Sample organisation',
}

// B-7: the Dashboard content for a signed-in user who belongs to 2+ organisations and has not chosen
// one yet (see OrganisationPickerLayout). This is membership, never assumption: it sends no assume header.
function SelectOrganisation({ fullName, memberships, showFictitiousOrganisations }: SelectOrganisationProps) {
	const api = useApi()
	const { fetchUserData } = useContext(userContext)
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState('')
	const [unlockTarget, setUnlockTarget] = useState<MembershipSummary | null>(null)

	const realMemberships = memberships.filter((membership) => !membership.fictitiousKind)
	// Never filter down to zero: if hiding fictitious organisations would leave nothing to pick, show them all.
	const visibleMemberships = (showFictitiousOrganisations === false && realMemberships.length > 0 ? realMemberships : memberships)
		.slice()
		.sort((a, b) => Number(!!a.fictitiousKind) - Number(!!b.fictitiousKind) || a.companyName.localeCompare(b.companyName))
	const hasNothingToShow = showFictitiousOrganisations === false && realMemberships.length === 0

	const onSelect = async (membership: MembershipSummary) => {
		setBusy(true)
		setError('')
		try {
			await api.post('organisations/select', {
				membershipId: membership.membershipId,
				_csrf: await getCSRFToken(),
			})
			await fetchUserData()
		} catch (err) {
			setError(getApiErrorMessage(err, 'Failed to select this organisation. Please try again.'))
		} finally {
			setBusy(false)
		}
	}

	if (hasNothingToShow) {
		return (
			<UniversalPageLayout
				documentTitle='Dashboard'
				heading={{ title: 'Dashboard' }}
				banners={
					<InlineAlert type='info' className='' data-testid='no-real-organisation-message'>
						<p>
							Your account has not yet been connected to a real organisation. You can{' '}
							<Link className={`underline text-algae-500 ${TEXT_LINK_FOCUS_RING}`} to={routes.companySettings.path}>request this in Account Settings</Link>.
							Alternatively, ask a colleague who uses Neoni® to add you. You will then be able to access your
							organisation&apos;s data in Neoni®.
						</p>
					</InlineAlert>
				}
			>
				<div className='p-lg bg-charcoal-600 rounded' data-testid='no-visible-organisation'>
					<h2 className='mt-3 text-4xl text-white'>Welcome to Neoni® Beta testing!</h2>
					<p className='mt-5 mb-2 text-white text-lg'>
						If you were expecting to see your sample and training organisations, then you can reveal them again in{' '}
						<Link className={`underline text-algae-500 ${TEXT_LINK_FOCUS_RING}`} to={routes.companySettings.path}>Account Settings</Link>.
					</p>
				</div>
			</UniversalPageLayout>
		)
	}

	return (
		<UniversalPageLayout
			documentTitle='Dashboard'
			heading={{ title: fullName ? `Welcome to Neoni, ${fullName}` : 'Welcome to Neoni' }}
		>
			<div className='max-w-[32rem] text-white p-1'>
				<div>Choose the organisation you want to work in</div>
				{error && (
					<InlineAlert type='error' className='mt-6'>{error}</InlineAlert>
				)}
				<ul className='mt-6 space-y-3'>
					{visibleMemberships.map((membership) => (
						<li key={membership.membershipId} className={`rounded p-4 flex items-center justify-between gap-4 ${membership.fictitiousKind ? '' : 'bg-charcoal-600'}`}
							data-fictitious={membership.fictitiousKind ? 'true' : undefined}
							style={membership.fictitiousKind ? { backgroundImage: dashedBorderBackground() } : undefined}
						>
							<div>
								<div>{membership.companyName}</div>
								<div className='text-charcoal-300 text-sm'>{ROLE_LABELS[membership.roleName] ?? membership.roleName}</div>
								{membership.fictitiousKind && <div className='text-charcoal-300 text-sm'>{FICTITIOUS_KIND_LABELS[membership.fictitiousKind]}</div>}
								{membership.companyLocked && <div className='mt-1 text-sm'>This organisation is currently locked</div>}
							</div>
							<div className='flex flex-col gap-2 shrink-0'>
								<Button
									type='Primary'
									size='sm'
									text='Open'
									disabled={busy || membership.companyLocked}
									onClick={() => onSelect(membership)}
								/>
								{membership.companyLocked && (
									<Button
										type='Outlined'
										size='sm'
										text='Request unlock'
										onClick={() => setUnlockTarget(membership)}
									/>
								)}
							</div>
						</li>
					))}
				</ul>
			</div>
			{unlockTarget && (
				<OrganisationUnlockRequestModal
					open
					companyId={unlockTarget.companyId}
					companyName={unlockTarget.companyName}
					onClose={() => setUnlockTarget(null)}
				/>
			)}
		</UniversalPageLayout>
	)
}

export default SelectOrganisation
